import React from 'react'
import { useModal, useSearch } from 'hooks'
import { useDispatch, useSelector } from 'react-redux'
import { useSendTestsInvitationsModal } from 'components/modals/SendTestsInvitationsModal/hooks'
import { useCandidateState } from 'components/pages/Jobs/JobDetails/hooks'
import { getCurrentAccount } from 'ducks/auth'
import { useSendTestsRemindersModal } from 'components/modals/SendTestsRemindersModal/hooks'
import * as R from 'ramda'
import { DEFAULT_JOB_SEEKER_STATE } from '../../helpers'
import { candidates } from 'orm'
import { groupForSending } from 'utils/sending'

export const useInvitesCareer = () => {
  const dispatch = useDispatch()
  const [search, setSearch] = useSearch()

  const data = useSelector(state => state.orm.candidates)
  const { items, meta: { selected } = {} } = data ?? {}

  const {
    state: { candidate },
    handlers: { setCandidate },
  } = useCandidateState()

  const {
    isOpen: isOverviewModalOpen,
    handleOpen: handleOverviewModalOpen,
    handleClose: handleOverviewModalClose,
  } = useModal()

  const {
    isOpen: isDeleteJobSeekerOpen,
    handleOpen: handleDeleteJobSeekerOpen,
    handleClose: handleDeleteJobSeekerClose,
  } = useModal()

  const { invitees, remindees, expired } = React.useMemo(
    () => groupForSending(items, selected),
    [items, selected],
  )

  const isNoInvitees = R.isEmpty(invitees)
  const isNoRemindees = R.isEmpty(remindees)
  const isNoExpired = R.isEmpty(expired)

  const [isTargetingExpired, setIsTargetingExpired] = React.useState(false)

  const handleResetJobSeeker = () => setCandidate(DEFAULT_JOB_SEEKER_STATE)

  const handleOverviewModalCloseWithReset = React.useCallback(() => {
    handleResetJobSeeker()
    handleOverviewModalClose()
  }, [handleResetJobSeeker, handleOverviewModalClose])

  const handleDeleteJobSeekerCloseWithReset = React.useCallback(() => {
    handleResetJobSeeker()
    handleDeleteJobSeekerClose()
  }, [handleResetJobSeeker, handleDeleteJobSeekerClose])

  const account = useSelector(getCurrentAccount)
  const workspaceId = account?.talent_assessment_workspace?.id
  const deadline = R.propOr(
    '',
    'deadline',
    account?.talent_assessment_workspace,
  )

  const handleDeleteJobSeeker = React.useCallback(
    (jobSeeker, { postSuccessAction } = {}) => {
      dispatch(
        candidates.deleteStart({
          pk: jobSeeker.id,
          postSuccessAction,
        }),
      )
    },
    [dispatch],
  )

  const handleCopy = () => {
    console.log('handleCopy')
  }

  const handleSendInvites = () => {
    console.log('send invites')
  }

  const handleDelete = React.useCallback(
    jobSeeker => {
      setCandidate(jobSeeker)
      handleDeleteJobSeekerOpen()
    },
    [handleDeleteJobSeekerOpen],
  )

  const handleEdit = React.useCallback(
    jobSeeker => {
      setCandidate(jobSeeker)
      handleOverviewModalOpen()
    },
    [handleOverviewModalOpen],
  )

  const handleDeleteJobSeekerConfirm = React.useCallback(() => {
    handleDeleteJobSeeker(candidate, {
      postSuccessAction: () => {
        handleResetJobSeeker()
        handleDeleteJobSeekerClose()
      },
    })
  }, [candidate, handleDeleteJobSeeker, handleDeleteJobSeekerClose])

  const handleOverviewModalConfirm = React.useCallback(
    (data, { onError, onSuccess } = {}) => {
      const isEditing = Boolean(candidate)

      const payload = {
        ...data,
        talent_assessment_workspace: workspaceId,
      }

      dispatch(
        isEditing
          ? candidates.patchStart({
              pk: candidate.id,
              data: payload,
              onFailureAction: onError,
              postSuccessAction: () => {
                onSuccess?.()
                handleOverviewModalCloseWithReset()
              },
            })
          : candidates.postStart({
              data: payload,
              onFailureAction: onError,
              postSuccessAction: () => {
                onSuccess?.()
                handleOverviewModalCloseWithReset()
              },
            }),
      )
    },
    [dispatch, candidate, workspaceId, handleOverviewModalCloseWithReset],
  )

  const {
    handleSendTestsInvitationsModalClose,
    handleSendTestsInvitationsModalConfirm,
    handleSendTestsInvitationsModalOpen,
    isSendTestsInvitationsModalOpen,
  } = useSendTestsInvitationsModal(
    { workspaceId },
    { handleSetCandidate: setCandidate },
  )

  const {
    handleSendTestsRemindersModalClose,
    handleSendTestsRemindersModalConfirm,
    handleSendTestsRemindersModalOpen,
    isSendTestsRemindersModalOpen,
  } = useSendTestsRemindersModal(
    { workspaceId },
    { handleSetCandidate: setCandidate },
  )

  const handleSendRemindersOpen = React.useCallback(() => {
    setIsTargetingExpired(false)
    handleSendTestsRemindersModalOpen()
  }, [handleSendTestsRemindersModalOpen])

  const handleSendExpiredOpen = React.useCallback(() => {
    setIsTargetingExpired(true)
    handleSendTestsRemindersModalOpen()
  }, [handleSendTestsRemindersModalOpen])

  const handleSendTestsMessage = React.useCallback(
    ({ candidate }) => {
      setCandidate(candidate)
      R.equals(candidate?.status, 'created')
        ? handleSendTestsInvitationsModalOpen()
        : handleSendTestsRemindersModalOpen()
    },
    [
      handleSendTestsInvitationsModalOpen,
      handleSendTestsRemindersModalOpen,
      setCandidate,
    ],
  )

  return {
    state: {
      account,
      search,
      candidate,
      invitees,
      remindees,
      expired,
      deadline,
      isSendTestsInvitationsModalOpen,
      isSendTestsRemindersModalOpen,
      isOverviewModalOpen,
      isDeleteJobSeekerOpen,
      isNoInvitees,
      isNoRemindees,
      isNoExpired,
      isTargetingExpired,
    },
    handlers: {
      setSearch,
      handleCopy,
      handleDelete,
      handleEdit,
      handleSendInvites,
      handleSendTestsMessage,
      handleSendTestsInvitationsModalClose,
      handleSendTestsInvitationsModalConfirm,
      handleSendTestsInvitationsModalOpen,
      handleSendTestsRemindersModalClose,
      handleSendTestsRemindersModalConfirm,
      handleSendTestsRemindersModalOpen,
      handleSendRemindersOpen,
      handleSendExpiredOpen,
      handleOverviewModalOpen,
      handleOverviewModalClose: handleOverviewModalCloseWithReset,
      handleOverviewModalConfirm,
      handleDeleteJobSeekerClose: handleDeleteJobSeekerCloseWithReset,
      handleDeleteJobSeekerConfirm,
    },
  }
}
